import React from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { mustBeNullOrNumber, multipleEmailsOptional} from 'form/validation';
import strings from 'localization/strings';
import { useField } from 'react-final-form';
import { getAvailablePaymentProviders } from 'logic/paymentLogic';
import paymentProviders from 'enums/paymentProviders';
import contactTypes from 'enums/contactTypes';
import { formatPercentage } from 'helpers/StringHelper';
import lockTypes from 'enums/lockTypes';

import Typography from '@material-ui/core/Typography';
import MarginBox from 'common/MarginBox';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { TextField, Checkboxes, showErrorOnBlur } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import StorageSiteLockConfigurationEditor from './lockConfiguration/StorageSiteLockConfigurationEditor';
import StorageSiteLockSystemUsersEditor from './StorageSiteLockSystemUsersEditor';

const EditStep5 = ({ showStepNumber = true, actor }) => {
    const { appContext } = useAppContext();
    const fieldNamePrefix = 'steps[5]';
    const getName = suffix => suffix ? `${fieldNamePrefix}.${suffix}` : fieldNamePrefix;
    const getValue = suffix => useField(getName(suffix)).input.value;

    const paymentProviderItems = appContext.payment.availablePaymentProviders
        .map(key => ({ label: paymentProviders[key].shortTitle, value: key }));

    const lockTypeItems = [{ key: 'none', title: strings.lockTypes.none.title }].concat(Object.values(lockTypes));

    const hasSynchronizableLockSystem = () => {
        const lockType = getValue('lockType');
        return lockType &&
               getValue('lockSystemUsers') &&
               (lockTypes[lockType]?.isSynchronizable ?? false);
    };

    return (
        <Box>
            <Typography variant="h3" display="block" gutterBottom>
                { showStepNumber && '5b. ' }
                {strings.storageSiteSettingsForSuperAdmin}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={strings.commissionRate}
                        name={getName('commissionRate')}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: mustBeNullOrNumber }}
                        helperText={strings.formatString(strings.storageSiteCommissionRateHelperText, formatPercentage(actor.commissionRate, appContext, { numberOfDecimals: 1}))}
                        disabled
                    />
                </Grid>

                <Grid item xs={12}>
                    <Checkboxes
                        label={strings.searchability}
                        name={getName('searchable')}
                        data={
                            { label: strings.storageSiteIsSearchable, value: true }
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.searchPriority}
                        name={getName('searchPriority')}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: mustBeNullOrNumber }}
                        helperText={strings.searchPriorityHelperText}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.regionSearchPriority}
                        name={getName('regionSearchPriority')}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: mustBeNullOrNumber }}
                        helperText={strings.regionSearchPriorityHelperText}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.distanceSearchAdjustment}
                        name={getName('distanceSearchAdjustment')}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: mustBeNullOrNumber }}
                        helperText={strings.distanceSearchAdjustmentHelperText}
                    />
                </Grid>

                {
                    getAvailablePaymentProviders(appContext).length > 1 &&
                    (
                        <Grid item xs={12}>
                            <Checkboxes
                                label={strings.paymentProvidersTitle}
                                name={getName('paymentProviders')}
                                data={paymentProviderItems}
                            />
                        </Grid>
                    )
                }

                <Grid item xs={12}>
                    <Checkboxes
                        label={strings.immediateBookings}
                        name={getName('allowImmediateBookings')}
                        data={
                            { label: strings.allowImmediateBookings, value: true }
                        }
                        helperText={strings.allowImmediateBookingsHelperText}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Checkboxes
                        label={strings.considerBookingsAsServicesTitle}
                        name={getName('considerBookingsAsServices')}
                        data={
                            { label: strings.considerBookingsAsServices, value: true }
                        }
                    />
                </Grid>
            </Grid>

            <MarginBox top={4}>
                <Typography variant="h4" display="block" gutterBottom>
                    {strings.lockConfigurations}
                </Typography>
            </MarginBox>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        select
                        label={strings.lockType}
                        name={getName('lockType')}
                        variant="outlined"
                    >
                        {
                            lockTypeItems.map(o => (
                                <MenuItem key={o.key} value={o.key}>
                                    {o.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Grid>

                <Grid item xs={12}>
                    <StorageSiteLockConfigurationEditor
                        fieldNamePrefix={getName('lockConfigurations')}
                        lockType={getValue('lockType')}
                    />
                </Grid>

                {
                    hasSynchronizableLockSystem() &&
                    (
                        <Grid item xs={12}>
                            <StorageSiteLockSystemUsersEditor
                                fieldNamePrefix={getName('lockSystemUsers')}
                            />
                        </Grid>
                    )
                }
            </Grid>

            <MarginBox top={4}>
                <Typography variant="h4" display="block" gutterBottom>
                    {strings.storageSiteContacts}
                </Typography>

                <Typography variant="body2" display="block" gutterBottom>
                    {strings.storageSiteContactsHelperText}
                </Typography>
            </MarginBox>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={strings.contactTypes.caretakerEmail}
                        name={getName(`contacts.${contactTypes.caretakerEmail.key}`)}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: multipleEmailsOptional }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.contactTypes.caretakerMobileTelephone}
                        name={getName(`contacts.${contactTypes.caretakerMobileTelephone.key}`)}
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.contactTypes.caretakerName}
                        name={getName(`contacts.${contactTypes.caretakerName.key}`)}
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={strings.contactTypes.roundReportEmail}
                        name={getName(`contacts.${contactTypes.roundReportEmail.key}`)}
                        variant="outlined"
                        showError={showErrorOnBlur}
                        fieldProps={{ validate: multipleEmailsOptional }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

EditStep5.propTypes = {
    showStepNumber: PropTypes.bool,
    actor: PropTypes.object
};

export default EditStep5;
